import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSearchDocumentAccessLogQuery } from '../app/api';

const brand = {
  deepBlue:  '#0a2947',
  tealBlue:  '#0069a6',
  goldenrod: '#d09b2c',
  charcoal:  '#58585b',
  softGray:  '#e9e6e1',
  green:     '#1a7f4e',
  red:       '#c0392b',
};

const PAGE_SIZE = 50;

// The two access actions, then the three emails we send a client. Emails share
// a colour because they are one family — what differs is which email, which
// the label says.
const ACTION_STYLE = {
  view:           { label: 'View',       bg: 'rgba(0,105,166,0.10)',  fg: brand.tealBlue },
  download:       { label: 'Download',   bg: 'rgba(208,155,44,0.18)', fg: brand.goldenrod },
  email_invite:   { label: 'Invite sent', bg: 'rgba(88,88,91,0.10)',  fg: brand.charcoal },
  email_reminder: { label: 'Chased',      bg: 'rgba(88,88,91,0.10)',  fg: brand.charcoal },
  email_shared:   { label: 'Announced',   bg: 'rgba(88,88,91,0.10)',  fg: brand.charcoal },
  // Client hid one of their uploads. The file and its record are kept.
  remove:         { label: 'Removed',     bg: 'rgba(154,28,28,0.08)', fg: '#9A1C1C' },
};

function ActionBadge({ action }) {
  const s = ACTION_STYLE[action] || { label: action, bg: 'rgba(88,88,91,0.10)', fg: brand.charcoal };
  return (
    <span style={{
      background: s.bg, color: s.fg, padding: '3px 10px', borderRadius: '20px',
      fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap',
    }}>
      {s.label}
    </span>
  );
}
ActionBadge.propTypes = { action: PropTypes.string };

// Who the actor was. Anything unrecognised reads as the raw actor_type rather
// than falling through to "Employee" — in a compliance log, mislabelling who
// touched a document is worse than showing a value nobody has styled yet.
function actorLabel(row) {
  if (row.actor_type === 'b2b') return row.firm_name || 'B2B';
  if (row.actor_type === 'employee') return 'Employee';
  if (row.actor_type === 'client') return 'Client (their own document)';
  return row.actor_type || '—';
}

const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString('en-US', {
  month: '2-digit', day: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit',
}) : '—');

// Admin-only search over the client document access audit trail — who
// viewed/downloaded which client's file, when, plus who emailed the client.
// Server-side filtered + paginated (unlike most admin pages here) since this
// table can grow large.
export default function DocumentAccessLog() {
  const [qDraft, setQDraft] = useState('');
  const [q, setQ] = useState('');
  const [action, setAction] = useState('');
  const [actorType, setActorType] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(0);

  // Debounce the search box so we don't refetch on every keystroke.
  useEffect(() => {
    const id = setTimeout(() => setQ(qDraft.trim()), 300);
    return () => clearTimeout(id);
  }, [qDraft]);

  // Any filter change resets to page 1 — a stale offset past the new result
  // count would otherwise show an empty page.
  useEffect(() => { setPage(0); }, [q, action, actorType, dateFrom, dateTo]);

  const { data, isFetching, error } = useSearchDocumentAccessLogQuery({
    q: q || undefined,
    action: action || undefined,
    actor_type: actorType || undefined,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  });

  const rows = data?.rows || [];
  const total = data?.total || 0;
  const hasFilters = Boolean(q || action || actorType || dateFrom || dateTo);
  const clearAll = () => { setQDraft(''); setAction(''); setActorType(''); setDateFrom(''); setDateTo(''); };

  const rangeStart = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const rangeEnd = Math.min(total, (page + 1) * PAGE_SIZE);

  const selectStyle = {
    background: 'rgba(255,255,255,0.1)', color: 'white', border: '1px solid rgba(255,255,255,0.25)',
    borderRadius: '6px', padding: '4px 10px', fontSize: '0.8rem', cursor: 'pointer',
  };

  return (
    <div style={{ background: brand.softGray, minHeight: '100vh', padding: '1.5rem' }}>

      {/* Header */}
      <div style={{ background: brand.deepBlue, borderRadius: '12px', padding: '1.5rem 2rem', marginBottom: '1.5rem', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', bottom: 0, left: 0, width: '100%', height: '3px', background: brand.goldenrod }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <div style={{ background: 'rgba(208,155,44,0.15)', border: `2px solid ${brand.goldenrod}`, borderRadius: '12px', padding: '0.65rem', flexShrink: 0 }}>
            <i className="bi bi-clock-history" style={{ color: brand.goldenrod, fontSize: '1.5rem' }} />
          </div>
          <div>
            <p style={{ color: brand.goldenrod, fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '2px', margin: '0 0 4px' }}>
              Compliance
            </p>
            <h3 style={{ color: 'white', fontWeight: 800, margin: 0 }}>Document Access Log</h3>
            <p style={{ color: 'rgba(255,255,255,0.65)', margin: '4px 0 0', fontSize: '0.85rem' }}>
              Every time a document was viewed or downloaded - by an employee, a B2B firm
              user, or a client opening something we shared with them - and every email
              we sent a client, with who sent it.
            </p>
          </div>
        </div>
      </div>

      {/* Table */}
      <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 12px rgba(10,41,71,0.08)', overflow: 'hidden' }}>
        <div style={{ background: brand.deepBlue, borderBottom: `3px solid ${brand.goldenrod}`, padding: '0.75rem 1.25rem', display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
          <i className="bi bi-search" style={{ color: brand.goldenrod }} />
          <h5 style={{ color: 'white', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '1px', margin: 0 }}>Access events</h5>
          <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
            {isFetching ? 'Loading…' : total === 0 ? '0 events' : `Showing ${rangeStart}–${rangeEnd} of ${total}`}
          </span>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
            <input
              type="search"
              value={qDraft}
              onChange={(e) => setQDraft(e.target.value)}
              placeholder="Search client, accessed by, filename…"
              aria-label="Search"
              style={{ background: 'rgba(255,255,255,0.1)', color: 'white', border: '1px solid rgba(255,255,255,0.25)', borderRadius: '6px', padding: '4px 10px', fontSize: '0.8rem', width: '220px' }}
            />
            <select value={action} onChange={(e) => setAction(e.target.value)} aria-label="Action" title="Action" style={selectStyle}>
              <option value="" style={{ color: brand.charcoal }}>Any action</option>
              <option value="view" style={{ color: brand.charcoal }}>View</option>
              <option value="download" style={{ color: brand.charcoal }}>Download</option>
              {/* Emails we sent the client. 'Invite sent' is the one that
                  carries a temp password, so it's worth filtering alone. */}
              <option value="email_invite" style={{ color: brand.charcoal }}>Invite sent</option>
              <option value="email_reminder" style={{ color: brand.charcoal }}>Chased for documents</option>
              <option value="email_shared" style={{ color: brand.charcoal }}>Announced a document</option>
              <option value="remove" style={{ color: brand.charcoal }}>Removed an upload</option>
            </select>
            <select value={actorType} onChange={(e) => setActorType(e.target.value)} aria-label="Accessed by" title="Accessed by" style={selectStyle}>
              <option value="" style={{ color: brand.charcoal }}>Anyone</option>
              <option value="employee" style={{ color: brand.charcoal }}>Employee only</option>
              <option value="b2b" style={{ color: brand.charcoal }}>B2B only</option>
              {/* A client opening a document we shared with them. They cannot
                  reach anything else, so this is the whole of client access. */}
              <option value="client" style={{ color: brand.charcoal }}>Client only</option>
            </select>
            <input type="date" value={dateFrom} max={dateTo || undefined} onChange={(e) => setDateFrom(e.target.value)} title="From date" style={selectStyle} />
            <input type="date" value={dateTo} min={dateFrom || undefined} onChange={(e) => setDateTo(e.target.value)} title="To date" style={selectStyle} />
            {hasFilters && (
              <button type="button" onClick={clearAll} style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.4)', borderRadius: '6px', padding: '4px 10px', fontSize: '0.8rem', color: 'white', cursor: 'pointer', fontWeight: 700 }}>
                <i className="bi bi-x-circle me-1" />Clear
              </button>
            )}
          </div>
        </div>

        {error ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: brand.red }}>
            Could not load the access log. Please try again.
          </div>
        ) : isFetching && rows.length === 0 ? (
          <div style={{ padding: '3rem', textAlign: 'center' }}>
            <div className="spinner-border" style={{ color: brand.tealBlue }} />
          </div>
        ) : rows.length === 0 ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: brand.charcoal }}>
            <i className="bi bi-inbox" style={{ fontSize: '2.5rem', opacity: 0.4, display: 'block', marginBottom: '0.5rem' }} />
            {hasFilters ? 'No access events match these filters.' : 'No document access recorded yet.'}
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-hover mb-0" style={{ fontSize: '0.825rem' }}>
              <thead style={{ background: brand.softGray }}>
                <tr>
                  <th style={{ padding: '0.5rem 0.75rem', color: brand.deepBlue, fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.3px', whiteSpace: 'nowrap' }}>When</th>
                  <th style={{ padding: '0.5rem', color: brand.deepBlue, fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Client</th>
                  <th style={{ padding: '0.5rem', color: brand.deepBlue, fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Document</th>
                  <th style={{ padding: '0.5rem', color: brand.deepBlue, fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Action</th>
                  <th style={{ padding: '0.5rem', color: brand.deepBlue, fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Accessed by</th>
                  <th style={{ padding: '0.5rem 0.75rem', color: brand.deepBlue, fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.3px' }}>IP</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td style={{ padding: '0.5rem 0.75rem', verticalAlign: 'middle', color: brand.charcoal, whiteSpace: 'nowrap' }}>{fmtDateTime(r.created)}</td>
                    <td style={{ padding: '0.5rem', verticalAlign: 'middle' }}>
                      <div style={{ fontWeight: 700, color: brand.deepBlue }}>{r.client_name || '—'}</div>
                      {r.client_email && <div style={{ color: brand.charcoal, fontSize: '0.72rem' }}>{r.client_email}</div>}
                    </td>
                    <td style={{ padding: '0.5rem', verticalAlign: 'middle', color: brand.charcoal, wordBreak: 'break-word' }}>
                      {r.filename || '—'}
                      {/* Which way the document travelled. Only flagged for
                          documents WE sent — an unmarked row is a client
                          upload, which is what every row was before sharing
                          existed and what most rows still are. */}
                      {r.document_kind === 'shared' && (
                        <div style={{ color: brand.tealBlue, fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          <i className="bi bi-send me-1" />Shared with client
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '0.5rem', verticalAlign: 'middle' }}><ActionBadge action={r.action} /></td>
                    <td style={{ padding: '0.5rem', verticalAlign: 'middle' }}>
                      <div style={{ fontWeight: 600, color: brand.deepBlue }}>{r.actor_name || r.actor_email || '—'}</div>
                      <div style={{ color: brand.tealBlue, fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        {actorLabel(r)}
                      </div>
                    </td>
                    <td style={{ padding: '0.5rem 0.75rem', verticalAlign: 'middle', color: brand.charcoal, whiteSpace: 'nowrap' }}>{r.ip_address || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {total > PAGE_SIZE && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '0.5rem', padding: '0.75rem 1.25rem', borderTop: `1px solid ${brand.softGray}` }}>
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              style={{ background: 'transparent', border: `1px solid ${brand.charcoal}`, color: brand.charcoal, borderRadius: '6px', padding: '4px 10px', fontSize: '0.8rem', cursor: page === 0 ? 'not-allowed' : 'pointer', opacity: page === 0 ? 0.5 : 1 }}
            >
              <i className="bi bi-chevron-left" /> Prev
            </button>
            <button
              type="button"
              onClick={() => setPage((p) => (rangeEnd < total ? p + 1 : p))}
              disabled={rangeEnd >= total}
              style={{ background: 'transparent', border: `1px solid ${brand.charcoal}`, color: brand.charcoal, borderRadius: '6px', padding: '4px 10px', fontSize: '0.8rem', cursor: rangeEnd >= total ? 'not-allowed' : 'pointer', opacity: rangeEnd >= total ? 0.5 : 1 }}
            >
              Next <i className="bi bi-chevron-right" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
